/*
* Created on Apr 01, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import {useAuthService} from "./AuthServiceHook";
import {merge} from "lodash";
import axios from "axios";
import {useRegion} from "../context/RegionContext";

export const useOrganizationApi = () => {
    const {AUTHAPI} = useAuthService();
    const {url} = useRegion();

    const logout = () => {
        console.log('logout clear local storage');
        window.localStorage.removeItem('currentOrg');
        window.localStorage.removeItem('currentUser');
        window.localStorage.removeItem('access_token');
        // window.location = '/';
        AUTHAPI.logout()
    }

    const refreshAuth = async (failedRequest) => {
        if  (failedRequest?.response?.data.code === 1013001) {
            try {
                const res = await AUTHAPI.refresh_token();
                failedRequest.response.config.headers['Authorization'] = `Bearer ${sessionStorage.getItem('access_token')}`;
                return res;
            } catch (err) {
                throw err;
            }
        }
        else {
            throw failedRequest;
           // return Promise.reject(failedRequest);
        }

    }

    const apiRequestAuthRefreshAxiosWrapper = (axiosInstance) => {
        return axiosInstance.interceptors.response.use((response) => {
            return response;
        }, async (error)=> {
            const originalRequest = error.config;
            if ([400, 401, 403].includes(error?.response?.status) && !originalRequest._retry) {
                originalRequest._retry = true;
                try{
                    await refreshAuth(error);
                    return axiosInstance(originalRequest);
                } catch (e) {
                    throw e;
                    // return Promise.reject(e);
                }
            }
            throw error;
            // return Promise.reject(error);
        });
    }

    const OrganizationAPI = {
        apiVersion: 'v1.2',
        baseURL: url.get_org_api_url(),
        timeout: 1000,
        headers: {'X-QNAP-APP-ID': '', 'content-type': 'application/json'},
        getConfig: (path, method, params, data) => {
            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method: method,
                headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                url: url
            }
            if (params) {
                custom_config['params'] = params;
            }
            if (data) {
                custom_config['data'] = data;
            }
            return custom_config;
        },
        get: async (path, params=null, download=false, signal=null) => {
            const CancelToken = axios.CancelToken;
            const source = CancelToken.source();

            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method: 'get',
                headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                params: params,
                url: url,
                cancelToken: source.token
            }
            if (download) {
                custom_config['responseType'] = 'blob';
            }
            try {
                const axiosInstance = axios.create();
                apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                const { data } = await axiosInstance(custom_config);
                // const a = createAuthRefreshInterceptor(axiosInstance, refreshAuth, {
                //     statusCodes: [400, 401, 403],
                //     pauseInstanceWhileRefreshing: true
                // });
                // const { data } = await axiosInstance(custom_config);

                signal?.addEventListener('abort', () => {
                    source.cancel('Query was cancelled by React Query');
                });


                return data;
            } catch (err) {
                await OrganizationAPI.handle_error(err);
            }
        },
        post: async (path, params=null, input_data=null) => {
            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method: 'post',
                headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                url: url
            }
            if (params) {
                custom_config['params'] = params;
            }
            if (input_data) {
                custom_config['data'] = input_data;
            }
            const axiosInstance = axios.create();
            try {
                apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                const { data } = await axiosInstance(custom_config);
                return data;
            } catch (err) {
                await OrganizationAPI.handle_error(err);
            }
        },
        put: async (path, params=null, input_data=null) => {
            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method: 'put',
                headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                url: url
            }
            if (params) {
                custom_config['params'] = params;
            }
            if (input_data) {
                custom_config['data'] = input_data;
            }
            const axiosInstance = axios.create();
            apiRequestAuthRefreshAxiosWrapper(axiosInstance)
            const { data } = await axiosInstance(custom_config);
            return data;
        },
        delete: async (path, params=null, input_data=null) => {
            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method: 'delete',
                headers: {'content-type': 'application/json', 'Authorization': `Bearer ${token}`},
                url: url
            }
            if (params) {
                custom_config['params'] = params;
            }
            if (input_data) {
                custom_config['data'] = input_data;
            }
            const axiosInstance = axios.create();
            apiRequestAuthRefreshAxiosWrapper(axiosInstance)
            const { data } = await axiosInstance(custom_config);
            return data;
        },
        upload: async (path, form_data, method='post') => {
            const url = `${OrganizationAPI.baseURL}/${OrganizationAPI.apiVersion}/${path}`
            const token = OrganizationAPI.getToken();
            const custom_config = {
                method,
                headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data'}),
                url: url,
                data: form_data
            }
            const axiosInstance = axios.create();
            apiRequestAuthRefreshAxiosWrapper(axiosInstance)
            const { data } = await axiosInstance(custom_config);
            return data;
        },
        device: {
            device_api_url: `${url.get_myqnapcloud_url()}/v1.2/device`,
            get: async (path, params, signal) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url = path ? `${OrganizationAPI.device.device_api_url}/${path}` : `${OrganizationAPI.device.device_api_url}`
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'get',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);

                    signal?.addEventListener('abort', () => {
                        source.cancel('Query was cancelled by React Query');
                    });

                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            put: async (path, params, input_data) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url = path ? `${OrganizationAPI.device.device_api_url}/${path}` : `${OrganizationAPI.device.device_api_url}`
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'put',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    data: input_data,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            post: async (path, params, input_data) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url = path ? `${OrganizationAPI.device.device_api_url}/${path}` : `${OrganizationAPI.device.device_api_url}`
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'post',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    data: input_data,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            }
        },
        friend: {
            friend_api_url: `${url.get_oauth_api_url()}/v1.2/friend`,
            get: async (path, params) => {
                const url = path ? `${OrganizationAPI.friend.friend_api_url}/${path}` : `${OrganizationAPI.friend.friend_api_url}`
                const token = OrganizationAPI.getToken();
                params['access_token']= token;
                const custom_config = {
                    method: 'get',
                    // headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}), //this api does not allow header to have authorization will cause CORS
                    headers: {'X-QNAP-APP-ID': '', 'content-type': 'application/json'},
                    params: params,
                    url: url
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            }
        },
        q2: {
            q2_api_url: `${url.get_q2_api_url()}/v1`,
            get: async (path, params = null, download = false, signal = null) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url = `${OrganizationAPI.q2.q2_api_url}/${path}`;
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'get',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    url: url,
                    cancelToken: source.token
                }
                if (download) {
                    custom_config['responseType'] = 'blob';
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const {data, status} = await axiosInstance(custom_config);
                    signal?.addEventListener('abort', () => {
                        source.cancel('Query was cancelled by React Query');
                    });
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            post: async (path, params, input_data) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url =  `${OrganizationAPI.q2.q2_api_url}/${path}`;
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'post',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    data: input_data,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            put: async (path, params, input_data) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url =  `${OrganizationAPI.q2.q2_api_url}/${path}`;
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'put',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    data: input_data,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            patch: async (path, params, input_data) => {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();

                const url =  `${OrganizationAPI.q2.q2_api_url}/${path}`;
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'patch',
                    headers: merge(OrganizationAPI.headers, {'Authorization': `Bearer ${token}`}),
                    params: params,
                    data: input_data,
                    url: url,
                    cancelToken: source.token
                }
                try {
                    const axiosInstance = axios.create();
                    apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                    const { data } = await axiosInstance(custom_config);
                    return data;
                } catch (err) {
                    await OrganizationAPI.handle_error(err);
                }
            },
            delete: async (path, params = null, input_data = null) => {
                const url =  `${OrganizationAPI.q2.q2_api_url}/${path}`;
                const token = OrganizationAPI.getToken();
                const custom_config = {
                    method: 'delete',
                    headers: {'content-type': 'application/json', 'Authorization': `Bearer ${token}`},
                    url: url
                }
                if (params) {
                    custom_config['params'] = params;
                }
                if (input_data) {
                    custom_config['data'] = input_data;
                }
                const axiosInstance = axios.create();
                apiRequestAuthRefreshAxiosWrapper(axiosInstance)
                const {data} = await axiosInstance(custom_config);
                return data;
            },
        },
        handle_error: async (err) => {
            if (err.response?.data.code !== 1013001) {
                console.log(err.response);
            }
            if (err.response?.data.code === 'invalid_refresh_grant') {
                console.log(err.response);
                logout();
            }
            throw err;
        },
        getToken: () => {
            // if (!Cookies.get('oc_session_id')) {
            //     Cookies.remove('access_token');
            // }
            return sessionStorage.getItem('access_token');
        }
    }

    return {OrganizationAPI}
};
